import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { PanelLeftOpen, SquarePen, Crown, LogOut, Moon, Sun, Settings, Paintbrush, LayoutDashboard } from "lucide-react";
import { useAuth } from "../../../../context/AuthContext";
import { useTheme } from "../../../../context/ThemeContext";
import { useAdmin } from "../../../../context/AdminContext";
import { updateChatModel } from "../../../api";
import CustomizeOGFLEET from "./CustomizeOGFLEET";
import SettingsModal from "./Settings";
import OGFleetIcon from '../../../../assets/OGFLEET Logo(1).png';

const Navbar = ({ isOpen, toggleSidebar, startNewChat, model, setModel, chatId, userName, Email }) => {
  const { isDarkTheme, toggleTheme } = useTheme();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const navigate = useNavigate();
  const profileContainerRef = useRef(null);
  const { logout, user } = useAuth();
  const { isAdmin } = useAdmin();

  const handleLogout = () => {
    logout();
    navigate("/auth");
  };

  const toggleLogoutMenu = () => {
    setShowUserMenu(!showUserMenu);
  };

  const handleModelChange = async (e) => {
    {
      const selectedValue = parseInt(e.target.value, 10);
      const selectedText = e.target.options[e.target.selectedIndex].text;
      const response = await updateChatModel(chatId, selectedValue);

      if (response.status === "success") {
        setModel(selectedValue, selectedText);
      }
    }
  }

  const getUserAvatar = () => {
    if (user && user.profilePicture) {
      return user.profilePicture; // Return the URL or path of the profile picture
    }
    if (userName && userName !== "-") {
      return userName.charAt(0).toUpperCase();
    }
    if (user && user.name) { // Fallback to auth context
      return user.name.charAt(0).toUpperCase();
    }
    return "?"; // Final fallback
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        profileContainerRef.current &&
        !profileContainerRef.current.contains(event.target)
      ) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const handleLogoClick = () => {
    navigate("/");
  };

  return (
    <>
      <div className={`flex items-center p-[10px_20px] ${isDarkTheme ? 'bg-dark-background text-white' : 'bg-[#fffcfc] text-black'} sticky top-0 z-100 border-b ${isDarkTheme ? 'border-dark-border' : 'border-gray-200'} shadow-sm`}>
        {/* Sidebar toggle and new chat (hidden when sidebar is open) */}
        {!isOpen && (
          <>
            <div className="flex items-center mr-4 hidden sm:block">
              <img
                src={OGFleetIcon}
                alt="OGFLEET Icon"
                className="w-11 h-10 cursor-pointer"
                onClick={handleLogoClick}
              />
            </div>
            <div className="relative group">
              <PanelLeftOpen
                className={`w-6 h-6 ${isDarkTheme ? 'text-gray-300' : 'text-gray-700'} cursor-pointer mr-4 transition-transform duration-300 z-10`}
                onClick={toggleSidebar}
              />
              <div className={`absolute top-8 left-1/2 transform -translate-x-1/2 hidden group-hover:block ${isDarkTheme ? 'text-white bg-gray-800' : 'text-black bg-white'} p-2 rounded text-xs shadow-md`}>
                Open Sidebar
              </div>
            </div>

            <div className="relative group">
              <SquarePen
                className={`w-6 h-6 ${isDarkTheme ? 'text-gray-300' : 'text-gray-700'} cursor-pointer mr-4 transition-transform duration-300 z-10`}
                onClick={startNewChat}
              />
              <div className={`absolute top-8 left-1/2 transform -translate-x-1/2 hidden group-hover:block ${isDarkTheme ? 'text-white bg-gray-800' : 'text-black bg-white'} p-2 rounded text-xs shadow-md`}>
                New Chat
              </div>
            </div>
          </>
        )}

        {/* Model Selector */}
        <div className="relative inline-block">
          <select
            className={`${isDarkTheme ? 'bg-gray-800 text-white border-gray-700' : 'bg-white text-gray-700 border-gray-200'} border rounded-md p-2 pr-10 pl-4 text-base cursor-pointer ml-2 opacity-100 transition-opacity duration-400 ease-in-out z-10 appearance-none focus:outline-none`} // Increased pr-10 for more space
            value={model}
            onChange={handleModelChange}
          >
            <option value={1}>OG-Drilling</option>
            <option value={2}>OG-Reservoir</option>
            <option value={3}>OG-Production</option>
          </select>
          {/* Custom Dropdown Arrow */}
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <svg
              className={`w-4 h-4 ${isDarkTheme ? 'text-gray-400' : 'text-gray-500'}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>

        <div className=" flex items-center ml-auto">
          {/* Theme Toggle Button */}
          <div className="relative group mr-4">
            {isDarkTheme ? (
              <Sun
                className="w-6 h-6 text-yellow-300 cursor-pointer transition-transform duration-300 z-10"
                onClick={toggleTheme}
              />
            ) : (
              <Moon
                className="w-6 h-6 text-gray-700 cursor-pointer transition-transform duration-300 z-10"
                onClick={toggleTheme}
              />
            )}
            <div className={`absolute top-8 left-1/2 transform -translate-x-1/2 hidden group-hover:block ${isDarkTheme ? 'text-white bg-gray-800' : 'text-black bg-white'} p-2 rounded text-xs shadow-md`}>
              Toggle Theme
            </div>
          </div>

          {/* User Avatar and Logout */}
          <div className="relative ml-auto" ref={profileContainerRef}>
            <div className="relative group">
              <div
                className="w-8 h-8 rounded-full bg-amber-500 text-white flex items-center justify-center cursor-pointer font-medium text-base overflow-hidden"
                onClick={toggleLogoutMenu}
                title={userName || "Profile"}
              >
                {getUserAvatar()}
              </div>
            </div>
            {showUserMenu && (
              <div className={`absolute top-12 right-0 ${isDarkTheme ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-2 rounded-lg p-5 w-72 z-10 shadow-xl`}>
                <div className={`py-3 pb-4 px-4 border-b-2 ${isDarkTheme ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className="font-bold text-lg">{user?.name || userName || "User"}</div>
                  <div className={`text-sm ${isDarkTheme ? 'text-gray-300' : 'text-gray-600'} truncate`}>{user?.email || Email || "No email"}</div>
                </div>

                <div className="space-y-2 mt-2">
                  {isAdmin && (
                    <button
                      onClick={() => {
                        navigate('/admin');
                        setShowUserMenu(false);
                      }}
                      className={`flex items-center w-full text-left px-4 py-3 text-base cursor-pointer ${isDarkTheme ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} rounded-lg`}
                    >
                      <LayoutDashboard className="w-5 h-5 mr-3 text-amber-500" />
                      Admin Panel
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setShowCustomizeModal(true);
                      setShowUserMenu(false);
                    }}
                    className={`flex items-center w-full text-left px-4 py-3 text-base cursor-pointer ${isDarkTheme ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} rounded-lg`}
                  >
                    <Paintbrush className="w-5 h-5 mr-3 text-amber-500" />
                    Customize OGFLEET
                  </button>

                  <button
                    onClick={() => {
                      setShowSettingsModal(true);
                      setShowUserMenu(false);
                    }}
                    className={`flex items-center w-full text-left px-4 py-3 text-base cursor-pointer ${isDarkTheme ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} rounded-lg`}
                  >
                    <Settings className="w-5 h-5 mr-3 text-amber-500" />
                    Settings
                  </button>

                  <button
                    onClick={() => {
                      navigate('/upgrade');
                      setShowUserMenu(false);
                    }}
                    className={`flex items-center w-full text-left px-4 py-3 text-base cursor-pointer ${isDarkTheme ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} rounded-lg`}
                  >
                    <Crown className="w-5 h-5 mr-3 text-amber-500" />
                    Upgrade Plan
                  </button>

                  <div className={`${isDarkTheme ? 'border-dark-border' : 'border-gray-200'} border-t-2 mt-2`}></div>

                  <button
                    onClick={handleLogout}
                    className={`flex items-center w-full text-left px-4 py-3 text-base cursor-pointer ${isDarkTheme ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} rounded-lg`}
                  >
                    <LogOut className="w-5 h-5 mr-3" />
                    Log out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Modals */}
      {showCustomizeModal && (
        <CustomizeOGFLEET
          isOpen={showCustomizeModal}
          onClose={() => setShowCustomizeModal(false)}
          isDarkTheme={isDarkTheme}
        />
      )}

      {showSettingsModal && (
        <SettingsModal
          isOpen={showSettingsModal}
          onClose={() => setShowSettingsModal(false)}
          isDarkTheme={isDarkTheme}
        />
      )}

    </>
  );
};

export default Navbar;